import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import { NO_PASTE_INPUT_PROPS } from 'modules/StockManagement/utils/stockLineValidation';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { lotKeyOf } from '../utils/bulkLotNdcStaging';
import { formatQty } from '../utils/bulkLotNdcStatus';
import { warningTint, warningTone } from '../utils/warningTone';

/**
 * POM-6121 — "Record a lot manually" for one reviewed row.
 *
 * The same panel the single-item Fill Product popup shipped in POM-6083, down to the wording, with
 * the two differences a bulk row forces:
 *
 *  - NO QUANTITY FIELD. A bulk row is one lot for the whole fill, so there is nothing to split and
 *    a field here could only invite a number that is wrong. The quantity is stated instead.
 *  - NO NDC CONTROL. The NDC comes from the "Select NDC" cards already above this panel, which
 *    become selectable in manual mode. A dropdown here would be a second source of truth for a
 *    choice the user has already been given.
 *
 * Presentational: every rule it renders arrives in `derived` from `deriveManualEntry`, so the
 * "Add lot" button and the dialog's own Apply gate read one evaluation of them rather than two.
 */
const ChangeNdcLotManualEntry = ({
  derived,
  fillQuantity,
  lotNumber,
  confirmLotNumber,
  onLotNumberChange,
  onConfirmLotNumberChange,
  onPickLot,
  onBudChange,
  onAddLot,
  onClose,
  isCheckingLotNumber,
}) => (
  <Box sx={{ mt: 2 }}>
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        /* Not `warning.light`: the theme's warning palette is the brand navy, so a warning drawn
           from it reads as a note. See utils/warningTone. */
        borderColor: (theme) => warningTint(theme, 0.5),
        borderRadius: 2,
        bgcolor: (theme) => warningTint(theme, 0.06),
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={1}>
        <Box>
          <Typography variant='subtitle2' fontWeight={700}>
            Record a lot manually
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            For stock inventory has no quantity for. Pick one of this product&apos;s zero-quantity
            lots or type a new lot number — a lot that still has quantity must be selected from the
            cards above instead. The quantity is recorded as {SYSTEM_DEFICIT.toLowerCase()} until an
            inventory user enters the receiving record.
          </Typography>
        </Box>
        <IconButton size='small' onClick={onClose} aria-label='Close manual entry'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Stack>

      <Stack direction='row' spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
        {/* freeSolo so a genuinely new lot can still be typed, with autoSelect so typed text is
            committed on blur. Without autoSelect the value is discarded unless the operator
            presses Enter — silently losing the entry, which is exactly how the Bulk Labels NDC
            filter broke. */}
        <Autocomplete
          freeSolo
          autoSelect
          size='small'
          options={derived.lots}
          // POM-6121 — an expired lot cannot be dispensed, so it cannot be claimed. Offered but
          // unselectable, so the operator can see it is on file and why it is not on offer.
          getOptionDisabled={(option) => typeof option !== 'string' && !!option.isExpired}
          value={lotNumber}
          onChange={(_event, value) => onPickLot(value)}
          onInputChange={(_event, value, reason) => {
            // Only typing clears a pick; 'reset' fires when a selection populates the input and
            // must not undo it.
            if (reason === 'input') onLotNumberChange(value);
          }}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.lotNumber)}
          isOptionEqualToValue={(option, value) =>
            (typeof option === 'string' ? option : option.lotNumber) ===
            (typeof value === 'string' ? value : value?.lotNumber)
          }
          renderOption={(props, option) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <MenuItem {...props} key={lotKeyOf(option)}>
              <Box>
                <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                  {option.lotNumber}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {option.expirationDate
                    ? `BUD ${String(option.expirationDate).slice(0, 10)}`
                    : 'No BUD recorded'}
                  {option.isExpired ? ' · expired' : ''}
                  {Number(option.unverifiedQty) > 0
                    ? ` · ${formatQty(option.unverifiedQty)} already in ${SYSTEM_DEFICIT.toLowerCase()}`
                    : ''}
                  {option.isFromThisBatch ? ' · entered for another row in this batch' : ''}
                </Typography>
              </Box>
            </MenuItem>
          )}
          renderInput={(params) => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              label='Lot number'
              required
              error={derived.isLotNumberInvalid}
              helperText={derived.lotHelperText}
            />
          )}
          sx={{ flex: '1 1 240px' }}
        />

        {/* POM-6179 — re-keyed by hand, and pasteable by nothing. A lot number read off a vial is
            the one value here no later screen can correct, and in a batch it is then offered to
            every sibling row of the product — so one typo propagates across the whole batch, not
            one row. Absent for a lot taken from the list below: that value is inventory's own, so
            there is no typo for a second typing to catch, and demanding one anyway is how a
            confirmation field becomes something operators learn to defeat. */}
        {derived.needsLotConfirmation && (
          <TextField
            size='small'
            label='Re-enter lot number'
            required
            value={confirmLotNumber}
            onChange={(event) => onConfirmLotNumberChange(event.target.value)}
            disabled={!lotNumber?.trim()}
            inputProps={NO_PASTE_INPUT_PROPS}
            error={derived.isConfirmMismatch}
            helperText={derived.confirmHelperText}
            sx={{ flex: '1 1 240px' }}
          />
        )}

        {/* Locked once a dated lot is in play: that row's BUD is part of its identity, and editing
            it here would describe a different lot than the one being claimed. Open for a picked lot
            that has none — that lot is non-dispensable until someone supplies the date, and the
            operator is holding the label. */}
        <TextField
          size='small'
          type='date'
          label='Expiration date (BUD)'
          value={derived.budValue}
          onChange={(event) => onBudChange(event.target.value)}
          disabled={derived.isBudLocked}
          InputLabelProps={{ shrink: true }}
          // Floors the picker at tomorrow, but only while the date is the operator's to type — one
          // adopted from a picked row is that lot's own. See deriveManualEntry.
          inputProps={derived.budMin ? { min: derived.budMin } : undefined}
          error={derived.isBudTooEarly}
          helperText={derived.budHelperText}
          sx={{ flex: '1 1 180px' }}
        />
      </Stack>

      {/* Stated rather than editable: a bulk row is always one lot for the whole fill. */}
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        sx={{
          mt: 1.5,
          px: 1.25,
          py: 1,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant='caption' color='text.secondary'>
          Quantity
        </Typography>
        <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
          {formatQty(fillQuantity)}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          — the whole fill. A bulk row is always one lot, so there is nothing to split.
        </Typography>
      </Stack>

      {derived.error && (
        <Alert
          severity='warning'
          sx={{
            mt: 1.5,
            py: 0.25,
            color: 'text.primary',
            bgcolor: (theme) => warningTint(theme, 0.14),
            border: '1px solid',
            borderColor: (theme) => warningTint(theme, 0.45),
            '& .MuiAlert-icon': { color: warningTone },
          }}
        >
          {derived.error}
        </Alert>
      )}

      <Stack direction='row' spacing={1} justifyContent='flex-end' sx={{ mt: 1.5 }}>
        <Button size='small' onClick={onClose}>
          Cancel
        </Button>
        {/* The label does not change while the check runs. It settles a few hundred milliseconds
            after each pause in typing, and a button whose words flicker on every pause reads as
            something going wrong; the spinner says the same thing without moving the target. */}
        <Button
          size='small'
          variant='contained'
          onClick={onAddLot}
          disabled={!derived.canAdd || isCheckingLotNumber}
          startIcon={isCheckingLotNumber ? <CircularProgress size={16} color='inherit' /> : null}
        >
          Add lot
        </Button>
      </Stack>
    </Box>

    <Stack direction='row' justifyContent='space-between' alignItems='baseline' sx={{ mt: 2 }}>
      <Typography variant='subtitle2'>Zero-quantity lots for this product</Typography>
      {/* Says the order out loud, because it is not the alphabetical one every other list on this
          screen uses — see offerableRecordableLots for why. */}
      <Typography variant='caption' color='text.secondary'>
        most recently moved first
      </Typography>
    </Stack>

    {derived.lots.length === 0 && (
      <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
        No zero-quantity lot on record for this NDC — type the lot number from the label instead.
      </Typography>
    )}

    <Stack spacing={1} sx={{ mt: 1 }}>
      {derived.lots.map((lot) => {
        // On identity, not on the printed number: one lot number legitimately repeats for an NDC
        // under different BUDs — the ambiguous case this panel names — and comparing the number
        // rendered both rows checked with no way to tell which was taken.
        const isSelected = !!derived.pickedLot && lotKeyOf(derived.pickedLot) === lotKeyOf(lot);
        // POM-6121 — a claim records a dispense, and an expired lot cannot be dispensed. Refused
        // here rather than on selection so the option reads as unavailable instead of failing
        // after it is chosen.
        const isExpired = !!lot.isExpired;

        return (
          /* A real <label> around a real radio, all sharing one name, so the list is a native
             radio group: Tab reaches it, the arrow keys move through it, and Space selects.
             `focus-within` puts the ring on the whole row, because the radio itself is a 20px
             dot in a full-width option and a ring on the dot alone is easy to miss. */
          <Paper
            key={lotKeyOf(lot)}
            component='label'
            variant='outlined'
            sx={{
              p: 1.5,
              display: 'block',
              cursor: isExpired ? 'not-allowed' : 'pointer',
              opacity: isExpired ? 0.55 : 1,
              borderColor: isSelected ? 'primary.main' : undefined,
              borderWidth: isSelected ? 2 : 1,
              '&:focus-within': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Radio
                checked={isSelected}
                onChange={() => onPickLot(lot)}
                disabled={isExpired}
                name='manual-recordable-lot'
                size='small'
                inputProps={{ 'aria-label': lot.lotNumber }}
              />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction='row' spacing={0.75} alignItems='center'>
                  <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                    {lot.lotNumber}
                  </Typography>
                  {lot.isFromThisBatch && (
                    <Chip label='THIS BATCH' size='small' variant='outlined' />
                  )}
                </Stack>
                <Typography variant='caption' color='text.secondary'>
                  {lot.expirationDate
                    ? `BUD ${String(lot.expirationDate).slice(0, 10)}`
                    : 'No BUD recorded'}
                  {lot.isExpired ? ' · expired' : ''}
                  {Number(lot.unverifiedQty) > 0
                    ? ` · ${formatQty(lot.unverifiedQty)} already in ${SYSTEM_DEFICIT.toLowerCase()}`
                    : ''}
                </Typography>
              </Box>
              <Typography variant='caption' color='text.disabled'>
                0 on hand
              </Typography>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  </Box>
);

ChangeNdcLotManualEntry.propTypes = {
  /** The output of `deriveManualEntry` — every rule this panel renders, evaluated once. */
  derived: PropTypes.shape({
    lots: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    pickedLot: PropTypes.shape({ lotNumber: PropTypes.string }),
    error: PropTypes.string,
    canAdd: PropTypes.bool,
    isBudLocked: PropTypes.bool,
    budValue: PropTypes.string,
    lotHelperText: PropTypes.string,
    budHelperText: PropTypes.string,
    /** POM-6179 — false once a lot is taken from the list; only a typed number is re-keyed. */
    needsLotConfirmation: PropTypes.bool,
    isLotNumberInvalid: PropTypes.bool,
    isConfirmMismatch: PropTypes.bool,
    confirmHelperText: PropTypes.string,
    budMin: PropTypes.string,
    isBudTooEarly: PropTypes.bool,
  }).isRequired,
  fillQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lotNumber: PropTypes.string,
  confirmLotNumber: PropTypes.string,
  onLotNumberChange: PropTypes.func.isRequired,
  onConfirmLotNumberChange: PropTypes.func.isRequired,
  onPickLot: PropTypes.func.isRequired,
  onBudChange: PropTypes.func.isRequired,
  onAddLot: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  /** True while the cross-NDC check for the typed number is still outstanding. */
  isCheckingLotNumber: PropTypes.bool,
};

ChangeNdcLotManualEntry.defaultProps = {
  isCheckingLotNumber: false,
  fillQuantity: '',
  lotNumber: '',
  confirmLotNumber: '',
};

export default ChangeNdcLotManualEntry;
